// SONAR glossary — locked to the DSD workbook vocabulary (see the Notes tab of
// the monthly export). Keyed by the `term` an <InfoPopover term="…" /> requests.
// Unknown terms render nothing, so the table can grow per section.

import classification from '../../sample_data/product_classification.json'

export interface GlossaryEntry {
  title: string
  body: string
}

// --- Business unit › Department › Category entries, generated from the
// committed classification (app/sample_data/product_classification.json, the
// output of the sonar-export skill's build_mapping.py) so the glossary cannot
// drift from the mapping. Keyed `category:<id>`; the InfoPopover on each
// category reads them.
interface ClassificationGlossaryEntry {
  name: string
  department: string
  unit: string
  classes: string[]
  productCount: number
  webProductCount: number
  examples: string[]
  text: string
}
const categoryEntries: Record<string, GlossaryEntry> = Object.fromEntries(
  Object.entries(classification.glossary as Record<string, ClassificationGlossaryEntry>).map(([id, g]) => {
    const parts: string[] = [`${g.unit} › ${g.department}.`]
    if (g.classes.length) parts.push(`Was: ${g.classes.join(', ')}.`)
    if (g.examples.length) parts.push(`Includes ${g.examples.slice(0, 6).join(', ')}${g.productCount > 6 ? ', …' : ''}.`)
    parts.push(g.text)
    return [`category:${id}`, { title: g.name, body: parts.join(' ') }]
  }),
)

export const glossary: Record<string, GlossaryEntry> = {
  // --- The classification (schema v2) ---------------------------------------
  businessUnit: {
    title: "Business unit",
    body: "The top of the Financial Products Classification: DSD Center (Planning Center, Lab, Delight) and DSD Education & Community (Events, Online, CORE). The same revenue as the old Revenue centre view, regrouped — totals are identical.",
  },
  department: {
    title: "Department",
    body: "The second level: Planning Center, Lab and Delight under DSD Center; Events, Online and CORE under Education & Community. Unclassified holds lines the grid has no column for yet and is never dropped or merged.",
  },
  category: {
    title: "Category",
    body: "The third level, one row per discipline or product family (Ortho, Milling, Lv 3, Tickets, …). Each category's products are listed in its drill-down and its definition in the glossary.",
  },
  stage: {
    title: "Stage",
    body: "Planning Center only. Planning & Motivating = planning, diagnostics and motivational design; Execution = guides, restorations, aligners and other delivery work. A second cut of the same Planning Center total, not an extra line.",
  },
  ...categoryEntries,
  // --- Core measures -------------------------------------------------------
  trueSale: {
    title: "True Sale",
    body: "Gross Sale minus PC credit consumed. The headline figure — sales that bring in new, collectable money. “Sale” always means True Sale.",
  },
  grossSale: {
    title: "Gross Sale",
    body: "The all-in value of every order placed, however funded — including work paid for with pre-paid PC credit.",
  },
  pcCreditConsumed: {
    title: "PC credit consumed",
    body: "Pre-paid retainer credit drawn down to fund Planning Centre work. Already booked as retainer revenue, so it is a memo only and is never added to a total.",
  },
  netSale: {
    title: "Net Sale",
    body: "True Sale minus refunds.",
  },
  revenue: {
    title: "Revenue (Inv)",
    body: "The invoiced, recognised portion of True Sale, booked on the invoice-date month.",
  },
  pipeline: {
    title: "Pipeline (Pipe)",
    body: "The part of True Sale ordered but not yet invoiced, shown on the order month.",
  },
  pipe: {
    title: "Pipeline (Pipe)",
    body: "The part of True Sale ordered but not yet invoiced, shown on the order month.",
  },
  cashCollected: {
    title: "Settled",
    body: "The part of True Sale recorded as paid against the order: card payments captured by the gateway (Braintree), invoices the website has marked paid (the manual capture — typically bank transfer, wire or SEPA), and the USD retainer + Fractional CMO billed through QuickBooks (accrual). The remainder is Outstanding. It is settlement recorded in the system, on the order month, gross of fees and FX — not cash cleared into the bank. Actual bank receipts (by payout date, net of fees and FX) live in the Cash Collections report and will differ.",
  },
  outstanding: {
    title: "Outstanding",
    body: "The part of True Sale that is billed but not yet recorded as paid — the complement of Settled (True Sale = Settled + Outstanding).",
  },
  ord: {
    title: "Ord",
    body: "True Sale on the order month — the month the order was placed.",
  },
  inv: {
    title: "Inv",
    body: "Revenue on the invoice month — the month the work was recognised.",
  },
  refunds: {
    title: "Refunds",
    body: "Refunds, netted inside card and invoice. True Sale is kept gross of refunds; Net Sale subtracts them.",
  },
  // --- The two splits ------------------------------------------------------
  splitByRecognition: {
    title: "Invoiced split",
    body: "True Sale = Revenue + Pipeline — but only over the full period (YTD), not in a single month. Revenue is booked on the invoice month, so lab/PC cases invoiced later than they were ordered push a month's Revenue ahead of (or behind) that month's order-based True Sale. For the split that ties every month, use Settled + Outstanding.",
  },
  splitByPayment: {
    title: "Payment split",
    body: "True Sale = Settled + Outstanding. This ties every month. (Settled is what the system records as paid against the order; the cash that actually clears the bank — net of fees and FX — is the Cash Collections report.)",
  },
  // --- Credit notes --------------------------------------------------------
  creditNotes: {
    title: "Credit notes",
    body: "Invoice reversals recorded by the accountant, not the website. Only “Other reversals” reduce sales; PC-credit credit notes are a memo (already stripped out via PC credit).",
  },
  netAfterCreditNotes: {
    title: "Net of credit notes",
    body: "Invoiced revenue after deducting reversal credit notes.",
  },
  creditNotesByDepartment: {
    title: "Credit notes by department",
    body: "Each deducted credit note traced to the product it reversed (through its invoice and the website order line) and placed in the Business unit › Department › Category classification, on the month it was raised. Business-unit totals equal the revenue table's credit notes; PC-credit memos are listed apart and never reduce sales.",
  },
  creditNotesUnallocated: {
    title: "Unallocated credit notes",
    body: "Credit notes the accountant filed under this business unit whose product belongs to another unit (for example event tickets filed under PC & Lab), or that could not be traced to a product. They stay in the unit so its total matches the accountant's figures; open the line to see each one.",
  },
  // --- Controls ------------------------------------------------------------
  currencyToggle: {
    title: "Currency",
    body: "EUR and USD are never blended. View each currency's own block, or Consolidated (€), which converts USD at each month's locked rate.",
  },
  consolidatedFx: {
    title: "Consolidated FX",
    body: "Each USD figure is converted to € at that month's locked average rate. An indicative management anchor, not a statutory figure.",
  },
  afterCnVsGrossToggle: {
    title: "Trend view",
    body: "Gross is invoiced revenue before credit notes; After CN nets out reversal credit notes; vs prior yr overlays last year's line.",
  },
  recognitionVsOrderToggle: {
    title: "Lens",
    body: "Order month is the sales view: True Sale by business unit on the month the order was placed, net of PC credit — the same basis as the headline. Invoiced month is the finance view: revenue recognised on the invoice date, net of PC credit, so work ordered in one month and invoiced in the next moves across. The toggle re-bases the table only — the headline is one True Sale either way.",
  },
  // --- DSD Center ----------------------------------------------------------
  labBlock: {
    title: "Lab",
    body: "The Lab department of DSD Center: Design (digital design of restorations and dentures), Printing (printed manufacture — guides, splints, mock-ups, shells) and Milling (milled restorations, dentures, implant bridges and abutments). Each category's glossary entry lists its products. Stage applies to Planning Center only.",
  },
  unclassifiedRemainder: {
    title: "Unclassified",
    body: "Planning Center + Lab lines the classification has not placed in a category yet — website products absent from the master product list. Shown as its own line so Planning Center + Lab + Unclassified always sum to the total; it empties as each product is placed.",
  },
  creditMadeAvailable: {
    title: "Made available",
    body: "Each clinic's monthly starting balance — the standing credit ceiling — summed across active clinics. A capacity figure, not cash granted, so utilisation is averaged across active months, not summed.",
  },
  pcCreditUsed: {
    title: "PC credit used",
    body: "PC credit drawn down via the payment gateway — the true total used to fund Planning Centre work. Can exceed the made-available ceiling when a clinic draws beyond its line.",
  },
  paidCardInvoice: {
    title: "Paid by card & invoice",
    body: "The cash portion of paid value — gross paid value minus the PC credit consumed.",
  },
  paidPcCredit: {
    title: "Paid by PC credit",
    body: "The portion of paid value settled from pre-paid retainer credit rather than new cash.",
  },
  included: {
    title: "Included work",
    body: "Zero-value lines bundled into the retainer. Units are counted; the money is shown at the work's retail (sale) price.",
  },
  includedRetailValue: {
    title: "Included retail value",
    body: "What the included/free work would have sold for at list price — the recovered value of bundled work.",
  },
  includedValueEst: {
    title: "Included value (estimated)",
    body: "The retail value of included work, distributed across months from a period total until the export carries it month by month. Any range still totals correctly.",
  },
  productionValue: {
    title: "Total production value",
    body: "The full economic output of the lab, however funded: new cash (card & invoice) + PC credit consumed + the retail value of included work.",
  },
}
